import React, { useState } from 'react';
import Appbar from 'components/appbar/Appbar';
import PageHeaderActions from 'components/page-header/PageHeaderActions';
import SurgerieOpenFilterBox from './SurgerieOpenFilterBox';
import { useFetchSurgeries } from './hooks/useFetchSurgeries';
import { SurgerieProvider } from './hooks/useSurgerie';
import { useDisplayMode } from 'hooks/useDisplayMode';
import Pagination from 'components/pagination/Pagination';
import NoData from 'components/no-data/NoData';
import TableLoading from 'components/loading/TableLoading';
import ModuleLoading from 'components/loading/ModuleLoading';
import SurgerieListModule from './list/module/SurgerieListModule';
import SurgerieListTable from './list/table/SurgerieListTable';
import PaginationProvider from 'hooks/pagination';
import InsideSaving from 'components/loading/InsideSaving';
import CloseSurgerieDialog from '../record/dialog/CloseSurgerieDialog';
import NewRecordAction from 'components/page-header/NewRecordAction';
import NewRecordButton from 'components/page-header/NewRecordButton';
import { usePermissionRules } from 'hooks/permissionRules';

const SurgerieOpen: React.FC = () => {
  const {
    surgeries,
    loading,
    orderedIndex,
    handleSort,
    selectedSurgerie,
    setSelectedSurgerie,
    filter,
    handleChangeFilter,
    page,
    rows,
    total,
    setPage,
    setRows,
    handleSearch,
    handleClose,
    closing,
  } = useFetchSurgeries();
  const [closeSurgerie, setCloseSurgerie] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [displayMode] = useDisplayMode();
  const { checkPermission } = usePermissionRules();
  const canCreate = checkPermission('surgeries.new');

  function onExited() {
    setSelectedSurgerie(null);
    setCloseSurgerie(false);
    setAnchorEl(null);
  }

  function updateList() {
    handleSearch();
    onExited();
  }

  function handleCloseSurgerie() {
    onExited();
    if (!selectedSurgerie) return;
    handleClose(selectedSurgerie).then(updateList);
  }

  return (
    <SurgerieProvider
      value={{
        anchorEl,
        setAnchorEl,
        selectedSurgerie,
        setSelectedSurgerie,
        setCloseSurgerie,
        filter,
        handleChangeFilter,
        handleSearch,
        loading,
      }}
    >
      {selectedSurgerie && closeSurgerie && (
        <CloseSurgerieDialog budget={selectedSurgerie} onExited={onExited} onConfirm={handleCloseSurgerie} />
      )}
      <Appbar
        title="Orçamentos cirúrgicos - Abertos"
        ActionsComponent={canCreate ? <NewRecordAction to="/surgeries/new" /> : undefined}
      />
      <PageHeaderActions
        title="Orçamentos cirúrgicos - Abertos"
        description="Visualize e gerencie os orçamentos cirúrgicos abertos."
        ActionComponent={canCreate ? <NewRecordButton label="Novo orçamento" to="/surgeries/new" /> : undefined}
      />

      <SurgerieOpenFilterBox />

      {closing && <InsideSaving message="Fechando orçamento..." />}

      {loading ? (
        displayMode === 'list' ? (
          <TableLoading />
        ) : (
          <ModuleLoading />
        )
      ) : surgeries.length === 0 ? (
        <NoData message="Nenhum orçamento cirúrgico encontrado" />
      ) : (
        <PaginationProvider rowsPerPage={rows}>
          {displayMode === 'list' ? (
            <SurgerieListTable surgeries={surgeries} />
          ) : (
            <SurgerieListModule surgeries={surgeries} />
          )}

          <Pagination
            count={total}
            currentPage={page}
            currentRowsPerPage={rows}
            onChangePage={page => setPage(page)}
            onChangeRowsPerPage={rows => setRows(rows)}
          />
        </PaginationProvider>
      )}
    </SurgerieProvider>
  );
};

export default SurgerieOpen;
